import { isError } from 'remeda';
import { z } from 'zod';

export const PAPERPAL_ONLINE_CHINA = 'paperpal-online-china';

const SignUpInputSchema = z.object({
  domain: z.string().default(''),
  lang: z.string().default('en'),
  ip: z
    .string()
    .regex(
      /^(?:\d{1,3}\.){3}\d{1,3}$|^([a-f0-9:]+:+)+[a-f0-9]+$/,
      'Must be a valid IPv4 or IPv6 address',
    )
    .or(z.literal(''))
    .default(''),
  email: z.string().email(),
  salutation: z.number().default(1),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  nativeName: z.string().optional(),
  phone: z.number().optional(),
  password: z.string().min(6),
  passwordConfirmation: z.string(),
  terms: z.literal(true),
  marketingConsent: z.boolean().optional(),
  timezone: z.string().default(''),
  country: z.string().length(2, 'Must be a 2-letter country code').default(''),
  organizationId: z.union([z.string(), z.number()]).optional(),
});
export type SignUpInput = z.infer<typeof SignUpInputSchema>;

export function createEditageApi({
  baseUrl,
  product,
  domain,
}: {
  baseUrl: string;
  product: string;
  domain: string;
}) {
  return {
    signUp: async (input: SignUpInput) => {
      try {
        let finalInput = input;
        const ip = finalInput.ip || '127.0.0.1';
        const country = finalInput.country || 'US';
        const timezone =
          finalInput.timezone ||
          (typeof Intl !== 'undefined'
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : 'UTC');

        finalInput = {
          ...finalInput,
          ip,
          country,
          timezone,
        };

        const validatedInput = SignUpInputSchema.parse(finalInput);
        const normalizedEmail = validatedInput.email.toLowerCase().trim();

        const payload: {
          domain: string;
          lang: string;
          ip: string;
          email: string;
          salutation: number;
          firstName: string;
          lastName: string;
          nativeName: string;
          tnc: boolean;
          newsletter: boolean;
          timezone: string;
          country: string;
          organization_id: string | number | null;
          phone: number | null;
          password: string;
        } = {
          domain: domain,
          lang: validatedInput.lang,
          ip: validatedInput.ip,
          email: normalizedEmail,
          salutation: 1,
          firstName: validatedInput.firstName,
          lastName: validatedInput.lastName,
          nativeName: validatedInput.nativeName ?? '',
          tnc: validatedInput.terms,
          newsletter: validatedInput.marketingConsent ?? false,
          timezone: validatedInput.timezone,
          country: validatedInput.country,
          organization_id: validatedInput.organizationId ?? null,
          phone: validatedInput.phone ?? null,
          password: validatedInput.password,
        };

        console.info('Signing up call', payload);

        const response = await fetch(`${baseUrl}/api/signup`, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            'X-PRODUCT-REFERER': product,
          },
          body: JSON.stringify(payload),
        });

        const json = await response.json();
        if (response.ok) {
          const result = SignUpResponse.parse(json);
          return result;
        } else {
          throw json;
        }
      } catch (error) {
        if (
          typeof error === 'object' &&
          error !== null &&
          ('errors' in error || 'message' in error)
        ) {
          throw error;
        }

        throw new Error(
          `Signup API failed: URL: ${baseUrl}/api/signup | error: ${
            isError(error) ? error.stack : JSON.stringify(error)
          }`,
        );
      }
    },

    changePassword: async (input: {
      // email: string;
      token: string;
      password: string;
      otp: string;
    }) => {
      try {
        const data = new URLSearchParams({
          // email: input.email,
          password: input.password,
          otp: input.otp,
          // token: input.token,
        });
        const response = await fetch(`${baseUrl}/api/user/changePassword`, {
          method: 'POST',
          body: data,
          headers: {
            accept: 'application/json',
            'X-PRODUCT-REFERER': product,
            Authorization: `Bearer ${input.token}`,
          },
        });

        const responseText = await response.text();
        if (!response.ok) {
          throw new Error('Failed to change password');
        }
        let json;
        try {
          json = JSON.parse(responseText);
        } catch (error) {
          throw new Error(`Failed to parse JSON response: ${error}`);
        }

        const result = ChangePasswordResponse.parse(json);
        if (isChangePasswordSuccess(result)) {
          return result;
        } else {
          throw new Error('Failed to change password');
        }
      } catch (error) {
        throw new Error(`Failed to change password: ${error}`);
      }
    },
  };
}

const SignUpSuccess = z.object({
  data: z
    .object({
      id: z.number(),
      email: z.string(),
      access_token: z.string().optional(),
    })
    .passthrough(),
  status: z.number(),
});
const SignUpErrors = z.object({
  errors: z.record(z.string(), z.string()),
  status: z.number(),
});
const SignUpResponse = z.union([SignUpErrors, SignUpSuccess]);
type SignUpResponse = z.infer<typeof SignUpResponse>;
type SignUpSuccess = z.infer<typeof SignUpSuccess>;

export function isSuccess(
  signUpResponse: SignUpResponse,
): signUpResponse is SignUpSuccess {
  return SignUpSuccess.safeParse(signUpResponse).success;
}

const ChangePasswordErrors = z.object({
  errors: z.record(
    z.string(),
    z.array(z.string()).transform((error) => error[0]),
  ),
});
const ChangePasswordSuccess = z.object({
  data: z.object({
    userId: z.number(),
  }),
  status: z.number(),
  message: z.string(),
});
const ChangePasswordResponse = z.union([
  ChangePasswordErrors,
  ChangePasswordSuccess,
]);
type ChangePasswordResponse = z.infer<typeof ChangePasswordResponse>;
type ChangePasswordSuccess = z.infer<typeof ChangePasswordSuccess>;

export function isChangePasswordSuccess(
  changePasswordResponse: ChangePasswordResponse,
): boolean {
  const isSuccess = ChangePasswordSuccess.safeParse(
    changePasswordResponse,
  ).success;
  return isSuccess;
}
